import { MouseEvent } from "react";
import { Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Color, DotLegalChip } from "@dotlegal/dotlegal-ui-components";

interface TreatmentChipBaseProps {
    label: string;
    color: Color;
    // Renders a dropdown caret inside the chip when it is the trigger for a status menu.
    showCaret?: boolean;
    // When set, the chip becomes the clickable trigger: pointer + hover feedback, and the handler
    // receives the DOM event so callers can anchor a popover to the chip. No business logic lives here.
    onClick?: (event: MouseEvent<HTMLElement>) => void;
}

// Presentational chip shared by the treatment-type chip and the mitigation-status chip: a coloured
// pill that can optionally be a clickable trigger with a caret. Label and colour are resolved by the
// callers; this owns only the rendering and the click affordance.
function TreatmentChipBase({ label, color, showCaret, onClick }: TreatmentChipBaseProps) {
    // DotLegalChip's only icon slot is MUI's trailing delete-icon, so the caret rides that slot to sit
    // inside the pill. MUI fires onDelete (with the event) and stops propagation when the icon is clicked,
    // so it is routed to the same open handler as the chip body.
    const chip = (
        <DotLegalChip
            value={label}
            color={color}
            size="small"
            isSelected
            // A no-op click flips DotLegalChip into its "clickable" styling (pointer cursor across the whole
            // pill, no ripple — its handler sits on a Box, not the MUI Chip). The real click + popover anchor
            // live on the wrapper Box below.
            onClick={onClick ? () => {} : undefined}
            deletionIcon={showCaret ? <KeyboardArrowDownIcon fontSize="small" /> : undefined}
            onDelete={showCaret && onClick ? (event) => onClick(event) : undefined}
        />
    );

    if (!onClick) return chip;

    return (
        <Box
            onClick={onClick}
            sx={{
                display: "inline-flex",
                transition: "filter 0.15s ease",
                // The caret uses the delete-icon slot, which is muted by default — match the chip's text colour.
                "& .MuiChip-deleteIcon": { color: "inherit", margin: 0 },
                "&:hover": { filter: "brightness(0.94)" },
            }}
        >
            {chip}
        </Box>
    );
}

export default TreatmentChipBase;
